import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import images from "../../Data/images";
import { motion } from "framer-motion";

const services = [
  {
    id: "parkingmanagementsolutions",
    title: "Parking Management Solutions",
    image: images.service9,
    description:
      "Argus Security International Inc. provides comprehensive parking management solutions designed to improve organization, maximize space utilization, and ensure smooth day-to-day parking operations for residential, commercial, and mixed-use properties.",
    highlights: [
      "Parking enforcement & compliance monitoring",
      "Permit management",
      "Visitor and resident parking control",
      "Fire route & emergency lane enforcement",
      "Accessible (AODA) space compliance",
      "Tow coordination (where authorized)",
      "Digital ticketing, reporting & photo documentation",
      "License Plate Recognition (LPR) solutions",
      "Signage review & placement recommendations",
    ],
    extraContent: {
      overview:
        "Our integrated approach combines professional personnel, technology, and customized site procedures to deliver safe, efficient, and well-controlled parking environments.",
      technology: [
        "Electronic reporting with real-time access",
        "Mobile devices for enforcement & documentation",
        "Optional LPR and access control integration",
        "Data analytics on violations, peak usage, and trends",
      ],
      benefits: [
        "Reduced unauthorized parking",
        "Improved traffic flow and safety",
        "Consistent, fair, and professional enforcement",
        "Custom site rules and operating procedures",
        "Enhanced tenant, resident, and visitor satisfaction",
      ],
      properties: [
        "Condominium",
        "Apartment complex",
        "Shopping plaza",
        "Hospital",
        "Campus",
        "Industrial property",
      ],
    },
  },
  {
    id: "eventsecurity",
    title: "Event Security Services",
    image: images.service5,
    description:
      "Argus Security International provides professional, reliable event security services designed to ensure safety, crowd control, and a seamless guest experience.",
    highlights: [
      "Uniformed event security personnel",
      "Access control & credential verification",
      "Crowd management & line control",
      "Perimeter monitoring & patrols",
      "Emergency response & incident management",
      "De-escalation & guest assistance",
      "Liaison with event staff and emergency services",
      "Digital incident reporting & post-event summaries",
    ],
    extraContent: {
      overview:
        "From corporate events and festivals to private functions and public gatherings, our trained security personnel deliver a visible, professional presence while maintaining a calm and respectful approach. We work closely with event organizers to understand site layouts, crowd flow, access points, and risk factors—allowing us to deploy the right personnel in the right locations.",
      services: [
        "Managing entry points",
        "Monitoring high-traffic areas",
        "Securing restricted zones",
        "Responding to incidents",
      ],
      tagline: "Professional Security. Reliable Coverage. Peace of Mind.",
    },
  },
  {
    id: "camerainstallations",
    title: "Camera Installation",
    image: images.service6,
    description:
      "Argus Security International delivers professional security camera installation tailored to commercial, industrial, and residential environments. We design, install, and configure reliable CCTV systems that enhance visibility, deter incidents, and provide clear, court-ready evidence when it matters most.",
    highlights: [
      "Site assessment & system design (coverage, angles, lighting, risk areas)",
      "Camera installation (fixed, dome, bullet, PTZ, LPR)",
      "Network & NVR/DVR setup with secure remote access",
      "Integration with access control, alarms, and monitoring",
      "Signage & compliance guidance",
      "Testing, training & handover",
    ],
    extraContent: {
      benefits: [
        "Clean, professional installs with minimal disruption",
        "High-definition imaging and reliable night performance",
        "Scalable systems built for future expansion",
        "Secure configurations and encrypted remote viewing",
        "Ongoing support, maintenance, and upgrades",
      ],
    },
  },
  {
    id: "uniformedsecurity",
    title: "Uniformed Security Guards",
    image: images.service1,
    description:
      "Uniformed security guards remain one of the most effective ways to prevent incidents, deter criminal activity, and maintain a safe environment for tenants, employees, and visitors.",
    highlights: [
      "Access control",
      "Routine property patrols",
      "Incident response",
      "Concierge and customer service",
      "Parking and property compliance monitoring",
      "Safety monitoring",
    ],
    extraContent: {
      overview:
        "At Argus Security International, our guards represent the visible presence of safety and control on your property.",
      technology: [
        "Digital patrol tracking",
        "Electronic incident reporting",
        "Real-time dispatch communication",
        "Body-worn cameras (where applicable)",
        "Mobile reporting tools",
        "Photo documentation of incidents",
      ],
      industries: [
        "Condominium & Residential Communities",
        "Commercial Office Buildings",
        "Construction Sites",
        "Industrial Facilities",
        "Retail & Shopping Centres",
        "Logistics & Truck Yards",
        "Events & Festivals",
      ],
    },
  },
  {
    id: "mobilepatrol",
    title: "Mobile Patrol Services",
    image: images.s7,
    description:
      "Mobile patrol services provide a highly effective security presence without the cost of full-time on-site guards.",
    highlights: [
      "Scheduled and randomized patrols",
      "Alarm response services",
      "Lock and unlock services",
      "Parking and property compliance monitoring",
      "Incident reporting",
    ],
    extraContent: {
      overview:
        "Argus patrol officers conduct scheduled and randomized patrols of your property using marked patrol vehicles to deter theft, vandalism, trespassing, and other criminal activity.",
      technology: [
        "GPS-tracked patrol vehicles",
        "Digital patrol verification",
        "Electronic incident reporting",
        "Real-time communication with dispatch",
        "Photo documentation of incidents",
        "Time-stamped patrol activity logs",
      ],
    },
  },
  {
    id: "conciergesecurity",
    title: "Concierge / Front Desk Security",
    image: images.service8,
    description:
      "Concierge security provides more than just a visible security presence—it creates a professional first point of contact for residents, guests, and visitors while maintaining control of building access and safety.",
    highlights: [
      "Access control and visitor management",
      "Resident assistance",
      "Lobby security monitoring",
      "Package and delivery oversight",
      "Property rule enforcement",
    ],
    extraContent: {
      overview:
        "Argus Security International provides trained concierge security officers who combine security awareness with professional customer service to support condominium communities, residential towers, and commercial buildings.",
      technology: [
        "Digital incident reporting",
        "Visitor and delivery logging systems",
        "CCTV monitoring systems",
        "Real-time communication with security dispatch",
        "Photo documentation of incidents",
      ],
    },
  },
  {
    id: "parkingenforcement",
    title: "Parking Enforcement",
    image: images.service13,
    description:
      "Argus Security International provides professional parking enforcement programs designed specifically for private properties residential communities and office complexes helping maintain order improve compliance and reduce complaints.",
    highlights: [
      "Unauthorized vehicles",
      "Visitor parking misuse",
      "Fire route violations",
      "Illegal overnight parking",
      "Abandoned vehicles",
    ],
    extraContent: {
      technology: [
        "Digital ticketing systems",
        "Photo documentation of violations",
        "Real-time incident reporting",
        "Electronic enforcement records",
        "Communication with property management",
      ],
    },
  },
  {
    id: "privateinvestigation",
    title: "Private Investigation Services",
    image: images.service12,
    description:
      "Argus Security International provides professional investigative services designed to uncover facts gather evidence and support informed decision making.",
    highlights: [
      "Surveillance investigations",
      "Corporate investigations",
      "Insurance investigations",
      "Background investigations",
      "Asset searches",
    ],
  },
  {
    id: "processserving",
    title: "Process Serving",
    image: images.service15,
    description:
      "Argus Security International provides professional process serving services to law firms corporations and legal professionals across Ontario.",
    highlights: [
      "Court documents",
      "Legal notices",
      "Landlord property documents",
      "Corporate documents",
      "Skip tracing support",
    ],
  },
  {
    id: "securitysystems",
    title: "Security Systems",
    image: images.service6,
    description:
      "Technology plays a critical role in modern security. CCTV surveillance and access control systems provide property owners with continuous monitoring improved accountability and stronger control over building access.",
    highlights: [
      "High definition CCTV cameras",
      "Exterior and interior camera installation",
      "Parking lot and perimeter monitoring",
      "Remote viewing capabilities",
      "Night vision surveillance",
    ],
  },
  {
    id: "lossprevention",
    title: "Loss Prevention",
    image: images.service14,
    description:
      "Argus Security International provides professional loss prevention officers trained to detect suspicious behavior prevent theft and support retail operations.",
    highlights: [
      "Shoplifting prevention",
      "CCTV monitoring",
      "Incident documentation",
      "Internal theft investigations",
      "Retail safety support",
    ],
  },
  {
    id: "firewatch",
    title: "Fire Watch",
    image: images.service11,
    description:
      "Argus Security International provides trained fire watch security personnel who monitor properties identify potential fire hazards and ensure rapid response in the event of an emergency.",
    highlights: [
      "Continuous patrol monitoring",
      "Fire hazard detection",
      "Emergency response",
      "Fire system outage coverage",
      "Detailed activity logs",
    ],
  },
  {
    id: "executiveelite",
    title: "Executive & Elite Security Services",
    image: images.service7,
    description:
      "Certain environments require a refined and professional security presence. Argus Security International offers elite security personnel dressed in professional business attire delivering safety access control and situational awareness.",
    highlights: [
      "Executive and VIP protection",
      "Discreet monitoring",
      "Access control",
      "Event security support",
      "Professional appearance officers",
    ],
  },
  {
    id: "executiveprotection",
    title: "Executive Protection",
    image: images.service7,
    description:
      "Executive protection services provide personal security and risk management for executives public figures and high profile individuals.",
    highlights: [
      "Close protection services",
      "Travel security support",
      "Risk assessments",
      "Event security oversight",
      "Secure transportation coordination",
    ],
  },
  {
    id: "gatehousesecurity",
    title: "Gatehouse Security",
    image: images.service4,
    description:
      "Gatehouse security officers manage entry points and monitor traffic flow to ensure only authorized individuals and vehicles access the property.",
    highlights: [
      "Vehicle access control",
      "Visitor registration",
      "Delivery management",
      "Traffic monitoring",
      "Incident reporting",
    ],
  },
];

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = services.find((s) => s.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!service) {
    return (
      <div className="text-center py-24 text-xl font-medium">
        Service not found.
      </div>
    );
  }

  return (
    <motion.div
      className="max-w-7xl mx-auto px-4 py-12"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Breadcrumbs */}
      <div className="text-sm text-gray-500 mb-4">
        <span
          onClick={() => navigate("/")}
          className="hover:underline cursor-pointer"
        >
          Home
        </span>{" "}
        /{" "}
        <span
          onClick={() => navigate("/#services")}
          className="hover:underline cursor-pointer"
        >
          Services
        </span>{" "}
        / <span className="text-[#c4122f]">{service.title}</span>
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-6 px-4 py-2 bg-[#c4122f] text-white rounded hover:bg-red-700 transition"
      >
        ← Back
      </button>

      {/* Main content */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-[400px] object-cover"
        />

        <div className="p-6 space-y-6">
          <h1 className="text-4xl font-bold text-[#c4122f]">{service.title}</h1>

          <p className="text-gray-700 text-lg leading-relaxed">
            {service.description}
          </p>

          {/* Optional: Add a Highlights Section */}
          {service.highlights && (
            <div className="mt-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Key Highlights:
              </h2>
              <ul className="list-disc pl-5 text-gray-700 space-y-1">
                {service.highlights.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Optional: Add more images */}
          {service.gallery && service.gallery.length > 0 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Gallery
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {service.gallery.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`${service.title} ${index + 1}`}
                    className="w-full h-56 object-cover rounded-lg shadow"
                  />
                ))}
              </div>
            </div>
          )}

          {service.extraContent && (
            <div className="mt-10 space-y-8">
              {service.extraContent.overview && (
                <p className="text-gray-700 text-lg leading-relaxed">
                  {service.extraContent.overview}
                </p>
              )}

              {/* Duties */}
              {service.extraContent.duties && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Duties & Responsibilities
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.duties.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Services */}
              {service.extraContent.services && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Our Services
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.services.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Technology */}
              {service.extraContent.technology && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Technology Used
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.technology.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Industries */}
              {service.extraContent.industries && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Industries We Protect
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.industries.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Properties */}
              {service.extraContent.properties && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Properties We Serve
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.properties.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Benefits */}
              {service.extraContent.benefits && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Benefits
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.benefits.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Situations */}
              {service.extraContent.situations && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Ideal Situations
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.situations.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Clients */}
              {service.extraContent.clients && (
                <div>
                  <h2 className="text-2xl font-semibold mb-3 text-gray-800">
                    Clients We Serve
                  </h2>
                  <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    {service.extraContent.clients.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tagline */}
              {service.extraContent.tagline && (
                <div className="bg-red-100 text-red-800 p-4 rounded-xl font-semibold text-lg">
                  {service.extraContent.tagline}
                </div>
              )}
            </div>
          )}

          {/* Optional: PDF, Video or Contact CTA */}
          <div className="mt-10 border-t pt-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Need this service?
            </h2>
            <button
              onClick={() => navigate("/contact")}
              className="mt-2 inline-block px-6 py-3 bg-[#c4122f] text-white rounded-xl hover:bg-red-700 transition"
            >
              Get in Touch
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ServiceDetail;
